/**
 * theme/modelCatalog.ts —— 「按提供方分组的模型目录」**单一源**（A1；源＝102 `components/model/catalog.js`(218) 逐条移植）
 *
 * 纪律（承旧树头注，逐条不丢）：
 *  · **零 DOM／零网络／零响应式／零时钟**（纯函数，可单测同源断言）；
 *  · **未知不猜**：缺 provider ⇒ 归「其它」组**不丢弃**；缺 `name` ⇒ 回落 `id`（**不造名**）；
 *    provider 文案**原样**（不做大小写归一、不发明品牌译名）；`priority` 缺失 ⇒ 置尾，**不猜序号**；
 *  · **不静默吞**：缺 `id`（不可选：请求体 `model` 正是该 id）的条目**计入 `skipped`** 如实上报。
 *  · 推理强度选项**单一源**——与后端白名单同集（旧树：`models_api._reasoning_effort_or_empty` 写闸 ＋
 *    `model_manager.REASONING_EFFORTS` 读面；**新树后端尚未提供该端点 ⇒ 见 §后端能力缺口**）。
 */

export const OTHER_PROVIDER = "other";
export const OTHER_PROVIDER_LABEL = "其它";
const UNKNOWN_PROVIDER_TOKEN = "unknown";

/** 宽松取串（null/undefined ⇒ ''；非串 ⇒ String()，不抛） */
function str(v: unknown): string {
  if (v === undefined || v === null) return "";
  return typeof v === "string" ? v : String(v);
}

export interface RawModel { id?: unknown; name?: unknown; provider?: unknown; priority?: unknown; thinking_effort?: unknown }
export interface ModelRow { id: string; name: string; provider: string; priority: number | null; effort: string; current?: boolean }
export interface ModelGroup { key: string; label: string; items: readonly ModelRow[] }

function idOf(m: RawModel | null | undefined): string { return str(m && m.id).trim(); }

/** provider 归类键：原样 trim；缺／空／字面 `unknown`（大小写不敏感）⇒ OTHER_PROVIDER */
export function providerKeyOf(m: RawModel | null | undefined): string {
  const raw = str(m && m.provider).trim();
  if (!raw || raw.toLowerCase() === UNKNOWN_PROVIDER_TOKEN) return OTHER_PROVIDER;
  return raw;
}

/** 组文案：兜底组＝「其它」，其余原样（零发明） */
export function providerLabelOf(key: string): string {
  return key === OTHER_PROVIDER ? OTHER_PROVIDER_LABEL : key;
}

/** priority 数值：有限数 ⇒ 该数；缺／非数／Infinity ⇒ null（排序置尾，绝不用下标冒充后端 priority） */
function priorityOf(m: RawModel | null | undefined): number | null {
  const raw = m ? m.priority : undefined;
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** 推理强度选择器**单一源**（与后端白名单同集；`''`⇒不下发思考参数） */
export const REASONING_EFFORT_OPTIONS: readonly { value: string; label: string }[] = Object.freeze([
  Object.freeze({ value: "", label: "不指定" }),
  Object.freeze({ value: "off", label: "off（关闭思考）" }),
  Object.freeze({ value: "low", label: "low" }),
  Object.freeze({ value: "high", label: "high" }),
  Object.freeze({ value: "max", label: "max" }),
]);
const EFFORT_VALUES: readonly string[] = Object.freeze(
  REASONING_EFFORT_OPTIONS.map((o) => o.value).filter((v) => Boolean(v)),
);

/** 推理强度归一：白名单外/缺失/非串 ⇒ ''（不臆造、不回落成某档） */
function effortOf(m: RawModel | null | undefined): string {
  const v = str(m && m.thinking_effort).trim().toLowerCase();
  return EFFORT_VALUES.includes(v) ? v : "";
}

function rowOf(m: RawModel): ModelRow {
  const id = idOf(m);
  return Object.freeze({
    id,
    name: str(m && m.name).trim() || id,   // 缺 name ⇒ 回落 id（不造名）
    provider: providerKeyOf(m),
    priority: priorityOf(m),
    effort: effortOf(m),
  }) as ModelRow;
}

/** 按提供方分组（① 组序＝首现序／后端 priority 序 ② 组内 priority 升序 null 置尾 ③ 缺/unknown ⇒ 「其它」 ④ 缺 id ⇒ skipped） */
export function groupByProvider(models: readonly RawModel[] | null | undefined):
  { groups: readonly ModelGroup[]; total: number; skipped: number } {
  const list = Array.isArray(models) ? models : [];
  const order: string[] = [];
  const buckets = new Map<string, ModelRow[]>();
  let skipped = 0;
  for (const raw of list) {
    if (!raw || typeof raw !== "object") { skipped += 1; continue; }
    if (!idOf(raw)) { skipped += 1; continue; }
    const key = providerKeyOf(raw);
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    (buckets.get(key) as ModelRow[]).push(rowOf(raw));
  }
  const groups = order.map((key) => {
    const items = (buckets.get(key) as ModelRow[]).slice();
    items.sort((a, b) => (a.priority === null ? Number.POSITIVE_INFINITY : a.priority)
                       - (b.priority === null ? Number.POSITIVE_INFINITY : b.priority));
    return Object.freeze({ key, label: providerLabelOf(key), items: Object.freeze(items) });
  });
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  return Object.freeze({ groups: Object.freeze(groups), total, skipped });
}

export interface Catalog {
  loaded: boolean; total: number; available: boolean; skipped: number;
  groups: readonly ModelGroup[]; providers: readonly string[];
  currentId: string; currentName: string; currentKnown: boolean; staleCurrent: boolean;
}

/** 目录派生（**唯一派生点**：模型位与设置面板喂同一返回值） */
export function deriveCatalog(o: { models?: readonly RawModel[] | null; current?: string | null } = {}): Catalog {
  const models = o.models ?? null;
  const loaded = Array.isArray(models);
  const base = groupByProvider(models);
  const currentId = str(o.current).trim();
  let currentName = "";
  let currentKnown = false;
  const groups = base.groups.map((g) => Object.freeze({
    key: g.key, label: g.label,
    items: Object.freeze(g.items.map((row) => {
      const hit = Boolean(currentId) && row.id === currentId;
      if (hit) { currentKnown = true; currentName = row.name; }
      return Object.freeze({ ...row, current: hit });
    })),
  }));
  return Object.freeze({
    loaded, total: base.total, available: loaded && base.total > 0, skipped: base.skipped,
    groups: Object.freeze(groups), providers: Object.freeze(groups.map((g) => g.key)),
    currentId,
    currentName: currentKnown ? currentName : currentId,   // 未命中 ⇒ 原样显示 id（零发明）
    currentKnown,
    staleCurrent: Boolean(loaded && currentId && !currentKnown && base.total > 0),
  });
}

/** 「模型可用」判据（单源）：**未加载 ≠ 无可用**（未知不猜，不引入预判性禁用） */
export function modelsUsable(catalog: Catalog | null): boolean {
  if (!catalog || !catalog.loaded) return true;
  return Boolean(catalog.available);
}

/** 展示位 `id → 显示名` 解析（未命中／无目录／空名 ⇒ null，调用方回落 id，不造名） */
export function modelNameOf(modelId: string, models: readonly RawModel[] | null | undefined): string | null {
  const id = str(modelId).trim();
  if (id === "") return null;
  if (!Array.isArray(models)) return null;
  for (const m of models) {
    if (str(m && m.id).trim() !== id) continue;
    const name = str(m && m.name).trim();
    return name === "" ? null : name;
  }
  return null;
}
